import {
  Button,
  cn,
  DataTable,
  DataTableColumnHeader,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  useDataTable,
} from '@data-delivery/d3s';
import type { UserEntity } from '@data-delivery/permission-ui-api';
import {
  sftpUsersKeys,
  useCurrentUserRoles,
  useDeleteSshKeyForUserMutation,
} from '@data-delivery/permission-ui-data-access-state';
import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { EllipsisIcon } from 'lucide-react';
import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { showErrorToast } from '../../utils/error-utils';
import {
  useBusinessGroupPermission,
  WithBusinessGroupPermission,
} from '../business-group-permission-context';
import { ConfirmDialog } from '../confirm-dialog';
import { ComposableSheet } from '../sheets/composable-sheet';
import { WithPermission } from '../with-permission';

export interface UserSshKeysListViewProps {
  user: UserEntity;
  tableClassName?: string;
}

export function UserSshKeysListView({ user, tableClassName }: UserSshKeysListViewProps) {
  const userDetailQuery = useQuery(sftpUsersKeys.byIdQuery(user.id));
  const sshKeysQuery = useQuery(sftpUsersKeys.sshKeysQuery(user.id));
  const deleteKey = useDeleteSshKeyForUserMutation();
  const { data: userRoles = [] } = useCurrentUserRoles();
  const bgPermissionContext = useBusinessGroupPermission();
  const isLoading = userDetailQuery.isLoading || sshKeysQuery.isLoading;
  const isError = userDetailQuery.isError || sshKeysQuery.isError;
  const error = userDetailQuery.error || sshKeysQuery.error;
  const keys = sshKeysQuery.data || [];

  const navigate = useNavigate();
  const location = useLocation();
  const { businessGroupId, id } = useParams<{ businessGroupId?: string; id?: string }>();
  const bgId = businessGroupId || id || '';

  const rows = keys;
  type Row = (typeof rows)[number];

  const columns = React.useMemo(() => {
    const cols: Array<ColumnDef<Row>> = [
      {
        id: 'actions',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Actions' />,
        cell: ({ row }) => (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='sm' aria-label='Actions'>
                <EllipsisIcon className='size-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='start'>
              {bgPermissionContext ? (
                <WithBusinessGroupPermission resource='users' action='update'>
                  <DropdownMenuItem
                    onClick={() => {
                      if (!bgId) {
                        return;
                      }

                      navigate(
                        `/business-groups/${bgId}/users/${user.id}/ssh-keys/${row.original.identifier}/edit`,
                        {
                          state: {
                            backgroundLocation: location.state?.backgroundLocation || location,
                          },
                        },
                      );
                    }}
                  >
                    Edit
                  </DropdownMenuItem>
                </WithBusinessGroupPermission>
              ) : (
                <WithPermission roles={userRoles} resource='users' action='update'>
                  <DropdownMenuItem
                    onClick={() => {
                      if (!bgId) {
                        return;
                      }

                      navigate(
                        `/business-groups/${bgId}/users/${user.id}/ssh-keys/${row.original.identifier}/edit`,
                        {
                          state: {
                            backgroundLocation: location.state?.backgroundLocation || location,
                          },
                        },
                      );
                    }}
                  >
                    Edit
                  </DropdownMenuItem>
                </WithPermission>
              )}
              {bgPermissionContext ? (
                <WithBusinessGroupPermission resource='users' action='update'>
                  <ConfirmDialog
                    keepTriggerDropdownOpen
                    trigger={<DropdownMenuItem>Delete</DropdownMenuItem>}
                    title='Delete SSH Key?'
                    description='This action will permanently remove the key. Continue?'
                    confirmText='Delete'
                    cancelText='Cancel'
                    destructive
                    onConfirm={async () => {
                      try {
                        deleteKey.mutate(
                          { userId: user.id, keyId: row.original.identifier },
                          {
                            onSuccess() {
                              toast.success('SSH key deleted');
                            },
                            onError(err: unknown) {
                              showErrorToast(err);
                            },
                          },
                        );
                      } catch (err: unknown) {
                        showErrorToast(err);
                      }
                    }}
                  />
                </WithBusinessGroupPermission>
              ) : (
                <WithPermission roles={userRoles} resource='users' action='update'>
                  <ConfirmDialog
                    keepTriggerDropdownOpen
                    trigger={<DropdownMenuItem>Delete</DropdownMenuItem>}
                    title='Delete SSH Key?'
                    description='This action will permanently remove the key. Continue?'
                    confirmText='Delete'
                    cancelText='Cancel'
                    destructive
                    onConfirm={async () => {
                      try {
                        deleteKey.mutate(
                          { userId: user.id, keyId: row.original.identifier },
                          {
                            onSuccess() {
                              toast.success('SSH key deleted');
                            },
                            onError(err: unknown) {
                              showErrorToast(err);
                            },
                          },
                        );
                      } catch (err: unknown) {
                        showErrorToast(err);
                      }
                    }}
                  />
                </WithPermission>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        size: 50,
        enableSorting: false,
      },
      {
        accessorKey: 'key',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Public Key' />,
        cell: ({ getValue }) => (
          <div className='max-w-xs break-all text-xs'>{getValue<string>()}</div>
        ),
      },
      {
        accessorKey: 'algorithm',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Algorithm' />,
        cell: ({ row }) => <span className='font-mono text-xs'>{row.original.algorithm}</span>,
      },
      {
        accessorKey: 'comments',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Comments' />,
        cell: ({ row }) => <span className='text-xs'>{row.original.comments || '—'}</span>,
      },
    ];
    return cols;
  }, [deleteKey, navigate, location, user.id, bgId, bgPermissionContext, userRoles]);

  const table = useDataTable<Row>({ data: rows, columns, getRowId: r => r.identifier });

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col items-end'>
        {bgPermissionContext ? (
          <WithBusinessGroupPermission resource='users' action='update'>
            <Button
              size='sm'
              onClick={() => {
                if (!bgId) {
                  return;
                }

                navigate(`/business-groups/${bgId}/users/${user.id}/ssh-keys/new`, {
                  state: { backgroundLocation: location.state?.backgroundLocation || location },
                });
              }}
            >
              New SSH Key
            </Button>
          </WithBusinessGroupPermission>
        ) : (
          <WithPermission roles={userRoles} resource='users' action='update'>
            <Button
              size='sm'
              onClick={() => {
                if (!bgId) {
                  return;
                }

                navigate(`/business-groups/${bgId}/users/${user.id}/ssh-keys/new`, {
                  state: { backgroundLocation: location.state?.backgroundLocation || location },
                });
              }}
            >
              New SSH Key
            </Button>
          </WithPermission>
        )}
      </div>
      {isLoading ? (
        <div className='text-sm text-muted-foreground'>Loading SSH keys…</div>
      ) : isError ? (
        <div className='text-destructive text-sm'>
          Failed to load SSH keys: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      ) : rows.length === 0 ? (
        <div className='text-sm text-muted-foreground'>No SSH keys.</div>
      ) : (
        <div className='flex flex-col rounded-md border'>
          <DataTable
            table={table}
            className={cn('h-[calc(100vh-250px)]', tableClassName)}
            emptyPlaceholderTitle='SSH Keys'
            emptyPlaceholderDescription='No SSH Keys'
          />
        </div>
      )}
    </div>
  );
}

export interface UserSshKeysSheetProps extends UserSshKeysListViewProps {
  children?: React.ReactNode;
}

export function UserSshKeysSheet({ user, children }: UserSshKeysSheetProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const handleClose = () => {
    const state = location.state as { backgroundLocation?: Location } | undefined;
    if (state?.backgroundLocation) {
      navigate(state.backgroundLocation.pathname, { replace: true });
    } else {
      navigate(-1);
    }
  };

  return (
    <ComposableSheet
      open
      onOpenChange={open => {
        if (!open) {
          handleClose();
        }
      }}
      title={`SSH Keys: ${user.loginId}`}
      description='Manage SSH keys for this user'
      side='right'
      widthClassName='w-full sm:max-w-5xl'
      footer={({ close }) => (
        <Button variant='outline' onClick={close}>
          Close
        </Button>
      )}
    >
      {children ?? <UserSshKeysListView user={user} />}
    </ComposableSheet>
  );
}
